import { useCallback, useMemo, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { creditsKeys } from '#creditFeature/models/credits/keys';
import { prospectQueryKeys } from '#leadOverviewFeature/models/prospectQueries/keys';
import { useSseStream } from '#lib/useSseStream';

import { agentRunStreamUrl } from '../api/agentRunsApi';
import { RUN_STATUS } from '../constants/conversation';
import { agentRunKeys } from '../models/keys';
import { useAgentRun, useAgentRunEvents, useAgentRunPreview } from '../models/queries';
import { appendRunEvent, buildConversation } from '../utils/buildConversation';

// Events after which the snapshot (status, phase, usage, mandate, openProposalSeq) is refetched.
const SNAPSHOT_EVENTS = new Set(['status', 'mandate', 'plan', 'profile', 'results', 'saved']);

/**
 * One conversation as the chat sees it: the snapshot, the stored events (replay) and the live
 * stream after them, folded into a view. Exactly one stream per visible conversation, bound to
 * the run id: it opens once that run's replay is in the cache and resumes from the last sequence
 * this hook has seen; events of another run never reach it.
 *
 * @param {string|null} runId
 * @param {{visible: boolean}} options - The popup shows this conversation (no stream otherwise)
 * @returns {{run: object|undefined, conversation: ReturnType<typeof buildConversation>, preview: Array<object>|undefined, isLoading: boolean, error: Error|null}}
 */
export function useConversation(runId, { visible }) {
	const qc = useQueryClient();
	const eventsQuery = useAgentRunEvents(runId, visible);
	const events = eventsQuery.data;
	const conversation = useMemo(() => buildConversation(events ?? []), [events]);

	const [replayed, setReplayed] = useState(null);
	// Derived during render, never from an effect: a new run id must not reuse the old cursor
	// for even one render.
	if (events !== undefined && replayed?.runId !== runId) setReplayed({ runId, after: conversation.lastSeq });
	const replayReady = replayed?.runId === runId;

	const lastSeqRef = useRef(0);
	lastSeqRef.current = Math.max(replayReady ? replayed.after : 0, conversation.lastSeq);

	const runQuery = useAgentRun(runId);
	const run = runQuery.data;
	const cancelled = (run?.status ?? conversation.status) === RUN_STATUS.CANCELLED;
	const streamEnabled = Boolean(runId) && visible && replayReady && !cancelled;

	const onEvent = useCallback(
		(type, data) => {
			if (!data || typeof data.seq !== 'number') return;
			qc.setQueryData(agentRunKeys.events(runId), (old) => appendRunEvent(old, data));
			if (SNAPSHOT_EVENTS.has(type)) {
				qc.invalidateQueries({ queryKey: agentRunKeys.detail(runId), exact: true });
				qc.invalidateQueries({ queryKey: agentRunKeys.draft(), exact: true });
			}
			if (type === 'commit' || type === 'saved') {
				qc.invalidateQueries({ queryKey: creditsKeys.wallet() });
				qc.invalidateQueries({ queryKey: prospectQueryKeys.all });
			}
		},
		[qc, runId],
	);

	useSseStream(streamEnabled ? `agent-run:${runId}` : null, {
		enabled: streamEnabled,
		resolveUrl: () => agentRunStreamUrl(runId, lastSeqRef.current),
		onEvent,
	});

	const previewIds = run?.previewCompanyIds?.length ? run.previewCompanyIds : conversation.previewCompanyIds;
	const previewQuery = useAgentRunPreview(runId, visible && previewIds.length > 0);

	return {
		run,
		conversation,
		preview: previewQuery.data,
		isLoading: Boolean(runId) && (runQuery.isPending || (visible && eventsQuery.isPending)),
		error: runQuery.error ?? eventsQuery.error ?? null,
	};
}
